import type { DriverDataApi } from "@App/apis/rest/ayvens/driverData/interfaces";
import { DriverDataSuccessResponse } from "@App/apis/rest/ayvens/driverData/responses/driverDataSuccessResponse";
import { Loader } from "@App/Components/Loader";
import { actionCreators, type State } from "@App/state";
import { setDriverData } from "@App/state/reducers/auth";
import { Role } from "@App/types/ayvens/role";
import { VehicleType } from "@App/types/ayvens/vehicleType";
import { fuelTypeIsBevForAyvens } from "@App/utils/conditionals/combinations";
import { bindActionCreators } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { type FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useSearchParams } from "react-router-dom";

interface DriverDataRequiredRoutesProps {
  driverDataApi: DriverDataApi;
  onDriverDataMissing: (role: Role) => void;
}

/**
 * Determines if driver data exists in the store.
 * If no driver data exists, onDriverDataMissing is called.
 * Use onDriverDataMissing to redirect to a driver selection form.
 *
 * Important: When using React Dom Router, onDriverDataMissing needs an absolute paths (e.g. /partner/ayvens/xxx).
 * Otherwise, there might be problems when driver data is reset in subfolders.
 *
 * @param driverDataApi The API to get the driver data from.
 * @param onDriverDataMissing The function to call when driver data is missing.
 * @constructor
 */
export const DriverDataRequiredRoutes: FunctionComponent<DriverDataRequiredRoutesProps> = ({
  driverDataApi,
  onDriverDataMissing,
}) => {
  const { t } = useTranslation();

  const driverData = useSelector((state: State) => state.auth.driverData);
  const role = useSelector((state: State) => state.auth.role);
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get("jwt");
  const [isLoading, setIsLoading] = useState(true);
  const [errorOccurred, setErrorOccurred] = useState(false);

  const dispatch = useDispatch();
  const {
    setNumberPlate,
    chooseBrand,
    changeBrandFilter,
    chooseGeneration,
    chooseMotorization,
    changeBevSupportFilter,
    changeCommercialVehicleSupportFilter,
  } = bindActionCreators(actionCreators, dispatch);

  const setStateFromDriverData = (driverData: DriverDataSuccessResponse) => {
    dispatch(setDriverData(driverData));
    setNumberPlate(driverData.vehicle.numberPlate);
    changeCommercialVehicleSupportFilter(false);

    if ([VehicleType.LCV].includes(driverData.vehicle.type)) {
      changeCommercialVehicleSupportFilter(true);
    }

    if (driverData.keyNumber) {
      chooseBrand(driverData.keyNumber.type.generation.brand);
      changeBrandFilter(driverData.keyNumber.type.generation.brand);
      chooseGeneration(driverData.keyNumber.type.generation);
      chooseMotorization(driverData.keyNumber.type);

      if (fuelTypeIsBevForAyvens(driverData.keyNumber.type.fuelType.id)) {
        changeBevSupportFilter(true);
      }
    }
  };

  useEffect(() => {
    if (driverData) return setIsLoading(false);
    if (role !== Role.DRIVER && !driverData) return onDriverDataMissing(role as Role);

    if (tokenParam === null) {
      Sentry.captureException(new Error("DriverDataRequiredRoutes: jwt token param is missing"));
      setErrorOccurred(true);
      return;
    }

    driverDataApi
      .getFromJwt(tokenParam)
      .then((result) => {
        setErrorOccurred(false);
        setStateFromDriverData(result);
      })
      .catch((error) => {
        Sentry.captureException(error);
        setErrorOccurred(true);
      })
      .finally(() => setIsLoading(false));
  }, [role, driverData]);

  if (errorOccurred)
    return (
      <div className="py-12 flex flex-col gap-4 justify-center items-center text-center">
        <div className="text-center text-gray-500 text-xl font-bold">
          {t("driverDataRoutes.error.title")}
        </div>
        <div className="text-center text-gray-500">
          <p> {t("driverDataRoutes.error.description")}</p>
          {t("driverDataRoutes.error.nextSteps")}
        </div>
        <div className="flex flex-col justify-center items-center">
          <div className="flex gap-1 mb-3">
            <a href={"mailto:" + t("contact.phone.link")} title="Eine Anfrage per E-Mail senden">
              {t("contact.phone.title")}
            </a>
          </div>
          <div>
            <a href={"mailto:" + t("contact.email.link")} title="Eine Anfrage per E-Mail senden">
              {t("contact.email.title")}
            </a>
          </div>
        </div>
      </div>
    );

  if (isLoading)
    return (
      <div className="py-8">
        <Loader />
      </div>
    );
  if (role !== Role.DRIVER) return null;
  return <Outlet />;
};
